<template>
  <div class="dash-root">
    <!-- ================= KPI ROW ================= -->
    <v-row class="dash-row-kpi" dense>
      <v-col cols="12" md="3">
        <v-card class="kpi-card kpi-card--income pa-4" flat>
          <div class="kpi-icon-badge">
            <v-icon size="22" color="#fff">mdi-currency-inr</v-icon>
          </div>
          <span class="kpi-title">Total Income</span>
          <span class="kpi-value">
            {{ formatCurrency(summary?.totalIncome) }}
          </span>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="kpi-card kpi-card--expense pa-4" flat>
          <div class="kpi-icon-badge">
            <v-icon size="22" color="#fff">mdi-trending-down</v-icon>
          </div>
          <span class="kpi-title">Total Expenses</span>
          <span class="kpi-value">
            {{ formatCurrency(summary?.totalExpense) }}
          </span>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="kpi-card kpi-card--profit pa-4" flat>
          <div class="kpi-icon-badge">
            <v-icon size="22" color="#fff">
              {{
                summary?.netProfit >= 0 ? "mdi-trending-up" : "mdi-trending-down"
              }}
            </v-icon>
          </div>
          <span class="kpi-title">Profit</span>
          <span class="kpi-value">
            {{ formatCurrency(summary?.netProfit) }}
          </span>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="kpi-card kpi-card--receivable pa-4" flat>
          <div class="kpi-icon-badge">
            <v-icon size="22" color="#fff">mdi-clock-outline</v-icon>
          </div>
          <span class="kpi-title">Amount Receivable</span>
          <span class="kpi-value">
            {{ formatCurrency(pending?.totalPendingAmount) }}
          </span>
        </v-card>
      </v-col>
    </v-row>

    <!-- ================= CHART / OVERDUE ROW ================= -->
    <v-row class="dash-row-charts" dense>
      <v-col cols="12" md="5" class="d-flex">
        <v-card class="pa-4 flex-grow-1 d-flex flex-column dash-panel" flat>
          <span class="section-title">Income vs Expenses</span>

          <div class="chart-wrapper">
            <canvas ref="lineChart"></canvas>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="d-flex">
        <v-card class="pa-4 flex-grow-1 d-flex flex-column dash-panel dash-panel--overdue" flat>
          <span class="section-title">
            <v-icon size="16" color="#b23a3a" class="mr-1">mdi-alert-circle-outline</v-icon>
            Overdue Invoices
            <span class="overdue-total" v-if="overdue?.overdueCount">
              ({{ formatCurrency(overdue.totalOverdueAmount) }})
            </span>
          </span>

          <div v-if="!overdue?.invoices?.length" class="overdue-empty">
            <v-icon size="32" color="#c8d1db">mdi-check-circle-outline</v-icon>
            <span>No overdue invoices</span>
          </div>

          <div v-else class="overdue-list">
            <div
              v-for="inv in overdue.invoices"
              :key="inv._id"
              class="overdue-row"
            >
              <div class="overdue-row-main">
                <span class="overdue-invoice-no">#{{ inv.invoiceNumber }}</span>
                <span class="overdue-customer">{{ inv.customer?.name }}</span>
              </div>
              <div class="overdue-row-side">
                <span class="overdue-amount">
                  {{ formatCurrency(inv.totalAmount - (inv.amountPaid || 0)) }}
                </span>
                <span class="overdue-due-date">Due {{ formatShortDate(inv.dueDate) }}</span>
              </div>
              <v-btn
                icon
                small
                class="overdue-view-btn"
                @click="$router.push({ path: `invoices/${inv._id}/view` })"
              >
                <v-icon size="18" color="#0a5a90">mdi-eye-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="3" class="d-flex">
        <v-card class="pa-4 flex-grow-1 d-flex flex-column dash-panel" flat>
          <span class="section-title">Expense Breakdown</span>

          <div v-if="!summary?.categories?.length" class="overdue-empty">
            <v-icon size="32" color="#c8d1db">mdi-chart-donut</v-icon>
            <span>No expenses recorded</span>
          </div>

          <div v-else class="chart-wrapper">
            <canvas ref="pieChart"></canvas>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ================= DUE TODAY / LOW STOCK ROW ================= -->
    <v-row class="dash-row-grid" dense>
      <v-col cols="12" md="8" class="d-flex">
        <v-card class="pa-4 flex-grow-1 d-flex flex-column dash-panel due-today-card" flat>
          <app-ag-grid
            class="due-today-grid"
            :title="
              isMobile
                ? 'Due Today'
                : 'Invoices Due Today (' + formatCurrency(dueToday.totalDueAmount) + ')'
            "
            :columnDefs="columnDefs"
            :rowData="dueToday?.invoices || []"
            :mobileCard="dueTodayMobileCard"
            @refresh="getTodayDueSummary"
            :newBtn="false"
            height="height: 100%"
          ></app-ag-grid>
        </v-card>
      </v-col>

      <!-- Calendar replaced by Low Stock panel — kept commented, not
           deleted, in case it's wanted back later. -->
      <!--
      <v-col cols="12" md="4" class="d-flex">
        <v-card class="pa-0 flex-grow-1 d-flex flex-column dash-panel" flat>
          <div class="custom-calendar">
            <div class="cal-header">
              <v-btn icon small @click="prevMonth">
                <v-icon small>mdi-chevron-left</v-icon>
              </v-btn>

              <span class="cal-title">
                {{ currentMonthName }} {{ currentYear }}
              </span>

              <v-btn icon small @click="nextMonth">
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>

            <div class="cal-grid">
              <div class="cal-day-name" v-for="d in days" :key="d">
                {{ d }}
              </div>

              <div
                v-for="(date, i) in calendarDays"
                :key="i"
                class="cal-cell"
                :class="{
                  today: isToday(date),
                  empty: !date,
                }"
              >
                <span v-if="date">{{ date.getDate() }}</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
      -->

      <v-col cols="12" md="4" class="d-flex">
        <v-card class="pa-4 flex-grow-1 d-flex flex-column dash-panel dash-panel--overdue" flat>
          <span class="section-title">
            <v-icon size="16" color="#b23a3a" class="mr-1">mdi-package-variant-closed-remove</v-icon>
            Low Stock
            <span class="overdue-total" v-if="lowStock?.length">
              ({{ lowStock.length }})
            </span>
          </span>

          <div v-if="!lowStock?.length" class="overdue-empty">
            <v-icon size="32" color="#c8d1db">mdi-check-circle-outline</v-icon>
            <span>No low stock products</span>
          </div>

          <div v-else class="overdue-list">
            <div
              v-for="p in lowStock"
              :key="p._id"
              class="overdue-row"
            >
              <div class="overdue-row-main">
                <span class="overdue-invoice-no">{{ p.productName }}</span>
                <span class="overdue-customer">HSN {{ p.hsn || "-" }}</span>
              </div>
              <div class="overdue-row-side">
                <span class="overdue-amount">{{ p.stockQuantity }} left</span>
                <span class="overdue-due-date">Threshold {{ p.lowStockThreshold }}</span>
              </div>
              <v-btn
                icon
                small
                class="overdue-view-btn"
                @click="$router.push({ path: `products/${p._id}/edit` })"
              >
                <v-icon size="18" color="#0a5a90">mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import AppAgGrid from "@/components/common/ag-grid.vue";

export default {
  name: "DashboardPage",
  components: { AppAgGrid },

  data() {
    return {
      lineChart: null,
      pieChart: null,
      currentDate: new Date(),
      days: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      columnDefs: [
        {
          headerName: "View",
          field: "_id",
          width: 80,
          minWidth: 70,
          cellRenderer: (params) => {
            return `
              <i
                class="mdi mdi-eye view-invoice"
                data-id="${params.data._id}"
                title="View Invoice"
                style="
                  cursor: pointer;
                  font-size: 18px;
                  color: #0a5a90;
                "
              ></i>
            `;
          },

          onCellClicked: (params) => {
            if (params.event.target.classList.contains("view-invoice")) {
              this.$router.push({ path: `invoices/${params.data._id}/view` });
            }
          },
        },
        {
          headerName: "Invoice Number",
          field: "invoiceNumber",
          width: 150,
          minWidth: 110,
        },
        {
          headerName: "Customer",
          field: "customer.name",
          width: 150,
          minWidth: 110,
        },
        {
          headerName: "Due Date",
          field: "dueDate",
          valueFormatter: (p) => {
            if (!p.value) return "-";

            const date = new Date(p.value);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();

            return `${day}-${month}-${year}`;
          },
          width: 150,
          minWidth: 110,
        },
        {
          headerName: "Subtotal",
          field: "subtotal",
          valueFormatter: (p) => {
            if (p.value == null) return "-";

            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
          width: 150,
          minWidth: 100,
        },
        {
          headerName: "GST",
          field: "gst",
          valueFormatter: (p) => {
            if (p.value == null) return "-";

            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
          width: 150,
          minWidth: 90,
        },
        {
          headerName: "Amount",
          field: "totalAmount",
          valueFormatter: (p) => {
            if (p.value == null) return "-";

            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
          width: 150,
          minWidth: 100,
        },
        { headerName: "User", field: "userId.name", width: 180, minWidth: 120 },
        { headerName: "User Email", field: "userId.email", width: 220, minWidth: 140 },
      ],
    };
  },

  async mounted() {
    await this.getDashboardSummary();
    // The pie chart's canvas is behind a v-if that depends on `summary`
    // (a Vuex-backed computed prop) — wait for Vue to flush the DOM
    // update from that data change before reading $refs.pieChart.
    this.$nextTick(() => this.renderCharts());
  },

  computed: {
    summary() {
      return this.$store.state.transactions.summary;
    },
    pending() {
      return this.$store.state.invoices.pending;
    },
    dueToday() {
      return this.$store.state.invoices.dueToday;
    },
    overdue() {
      return this.$store.state.invoices.overdue;
    },
    lowStock() {
      return this.$store.state.products.lowStock;
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    dueTodayMobileCard() {
      return {
        title: (row) => `#${row.invoiceNumber}`,
        subtitle: (row) => row.customer?.name || "-",
        fields: (row) => [
          { label: "Amount", value: this.formatCurrency(row.totalAmount) },
        ],
        actions: (row) => [
          {
            label: "View",
            icon: "mdi-eye-outline",
            color: "#0a5a90",
            onClick: () => this.$router.push({ path: `invoices/${row._id}/view` }),
          },
        ],
      };
    },
    currentMonthName() {
      return this.currentDate.toLocaleString("default", { month: "short" });
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      const firstDay = new Date(year, month, 1).getDay();
      const lastDate = new Date(year, month + 1, 0).getDate();

      const days = [];

      // empty slots
      for (let i = 0; i < firstDay; i++) {
        days.push(null);
      }

      // actual dates
      for (let i = 1; i <= lastDate; i++) {
        days.push(new Date(year, month, i));
      }

      return days;
    },
  },

  methods: {
    async getDashboardSummary() {
      try {
        await Promise.all([
          this.$store.dispatch("transactions/getTransactionSummary"),
          this.$store.dispatch("invoices/getPendingAmount"),
          this.getTodayDueSummary(),
          this.getOverdueSummary(),
          this.getLowStockSummary(),
        ]);
      } catch (e) {
        this.$toast?.error("Failed to load dashboard");
      }
    },

    async getTodayDueSummary() {
      await this.$store.dispatch("invoices/getTodayDueSummary");
    },

    async getOverdueSummary() {
      await this.$store.dispatch("invoices/getOverdueSummary");
    },

    async getLowStockSummary() {
      await this.$store.dispatch("products/getLowStock");
    },

    renderCharts() {
      if (!this.summary) return;

      const monthly = this.summary.monthly || [];
      const categories = this.summary.categories || [];

      if (this.lineChart) this.lineChart.destroy();
      if (this.pieChart) this.pieChart.destroy();

      // LINE CHART
      this.lineChart = new Chart(this.$refs.lineChart, {
        type: "line",
        data: {
          labels: monthly.map((i) => i.month),
          datasets: [
            {
              label: "Income",
              data: monthly.map((i) => i.income),
              borderColor: "#4caf50",
              tension: 0.4,
            },
            {
              label: "Expenses",
              data: monthly.map((i) => i.expense),
              borderColor: "#f44336",
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false, // 🔥 FIX
          plugins: {
            legend: { position: "bottom" },
          },
        },
      });

      // PIE CHART — canvas only renders (v-if) when there are categories
      // to chart; skip creation when it's not in the DOM, otherwise
      // Chart.js throws "can't acquire context from the given item".
      if (!categories.length || !this.$refs.pieChart) {
        this.pieChart = null;
        return;
      }

      this.pieChart = new Chart(this.$refs.pieChart, {
        type: "doughnut",
        data: {
          labels: categories.map((i) => i.name),
          datasets: [
            {
              data: categories.map((i) => i.amount),
              backgroundColor: ["#4caf50", "#ff9800", "#2196f3", "#f44336"],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false, // 🔥 FIX
          plugins: {
            legend: { position: "bottom" },
          },
        },
      });
    },

    formatCurrency(val) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(val || 0);
    },

    formatShortDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    prevMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1,
      );
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1,
      );
    },
    isToday(date) {
      if (!date) return false;
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },
  },

  watch: {
    summary() {
      this.$nextTick(() => {
        this.renderCharts();
      });
    },
  },
};
</script>

<style scoped>
/* ================= ROOT — fit viewport, no page scroll ================= */
.dash-root {
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 12px;
}

.dash-row-kpi {
  flex: 0 0 auto;
  margin: 0 !important;
}

/* Charts row is shorter; grid/calendar row takes the larger remaining share */
.dash-row-charts {
  flex: 0 0 300px;
  min-height: 0;
  margin: 0 !important;
}

.dash-row-grid {
  flex: 1 1 0;
  min-height: 0;
  margin: 0 !important;
}

.dash-row-charts > .col,
.dash-row-grid > .col {
  height: 100%;
}

/* ================= KPI CARDS ================= */
.kpi-card {
  position: relative;
  border-radius: 0;
  overflow: hidden;
  color: #fff;
  min-height: 84px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(15, 30, 50, 0.18);
}

.kpi-card--income {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.kpi-card--expense {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.kpi-card--profit {
  background: linear-gradient(135deg, #0a5a90 0%, #0d8478 100%);
}
.kpi-card--receivable {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}

.kpi-icon-badge {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-title {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  display: block;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  display: block;
  margin-top: 6px;
  letter-spacing: -0.01em;
}

/* ================= PANELS ================= */
.dash-panel {
  border-radius: 0;
  box-shadow: 0 4px 14px rgba(15, 30, 50, 0.08);
}

/* Due-today grid: stretch to fill the card via flex instead of a viewport
   calc(), so it stays correct across browser zoom levels (vh/px drift
   apart at non-100% zoom, which caused the grid to overflow/underflow).
   `.due-today-grid` lands on AppAgGrid's own root element (class
   inheritance), so the first rule targets itself, not a descendant. */
.due-today-card {
  min-height: 0;
  overflow: hidden;
}

.due-today-grid.app-ag-grid {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
}

.due-today-grid.app-ag-grid >>> .col {
  height: 100%;
}

.due-today-grid >>> .app-grid-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Vuetify's own .row has flex: 1 1 auto, so without this the toolbar row
   (first child) grows to share space equally with the grid row instead
   of sizing to its content. */
.due-today-grid >>> .app-grid-card > .row:first-child {
  flex: 0 0 auto;
}

.due-today-grid >>> .app-grid-card > .row:last-child {
  flex: 1 1 0;
  min-height: 0;
}

.due-today-grid >>> .app-grid-card > .row:last-child > .col {
  height: 100%;
}

/* ================= NARROW OR SHORT — allow natural scroll ================= */
/* The no-scroll single-screen layout above assumes the viewport is both
   wide enough (desktop breakpoint) AND tall enough to fit the KPI row +
   the 300px-floor charts row + a usable amount of grid row without
   squashing anything. That combined height floor is real: app-bar (50px)
   + .app-content padding (32px) + KPI row (~100px) + gaps (24px) +
   charts row floor (300px) = ~506px before the grid row gets anything
   at all. Any of several ordinary things push a viewport under that —
   a restored (non-maximized) browser window, OS taskbar/dock height,
   a fallback system font rendering text taller than expected if a web
   font fails to load — and the fixed layout has no slack to absorb it,
   so it overflows into a forced scrollbar instead. Below the safety
   threshold, fall back to the same natural-scroll layout already used
   on mobile so content stays fully visible and readable instead of
   silently breaking. */
@media (max-width: 960px), (max-height: 750px) {
  .dash-root {
    height: auto;
    display: block;
    overflow: visible;
  }

  .dash-row-kpi,
  .dash-row-charts,
  .dash-row-grid {
    flex: none;
    margin-bottom: 12px !important;
  }

  .dash-row-charts > .col,
  .dash-row-grid > .col {
    height: auto;
  }

  .dash-panel {
    min-height: 260px;
  }

  .due-today-card {
    height: auto;
    min-height: 360px;
  }

  .due-today-grid.app-ag-grid {
    height: auto;
  }

  .app-grid-cards {
    max-height: none;
  }

  .custom-calendar {
    height: auto;
    min-height: 320px;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  display: block;
}

.chart-wrapper {
  position: relative;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
}

.chart-wrapper canvas {
  position: absolute !important;
  width: 100% !important;
  height: 100% !important;
}

/* ================= CALENDAR ================= */
.custom-calendar {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.cal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.cal-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: 0.3px;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 4px;
  flex-grow: 1;
  min-height: 0;
}

.cal-day-name {
  font-size: 10px;
  text-align: center;
  color: #9ca3af;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding-bottom: 4px;
}

.cal-cell {
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8fafc;
  border: 1px solid transparent;
}

.cal-cell:hover {
  background: rgba(10, 90, 144, 0.08);
  border-color: rgba(10, 90, 144, 0.2);
  transform: scale(1.05);
}

.cal-cell.today {
  background: linear-gradient(150deg, #052f5e 0%, #0a5a90 35%, #0d8478 68%, #15a355 100%);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(10, 90, 144, 0.3);
}

.cal-cell.selected {
  border: 2px solid #0a5a90;
  font-weight: 600;
}

.cal-cell.empty {
  background: transparent;
  pointer-events: none;
}

/* ================= OVERDUE INVOICES ================= */
.dash-panel--overdue {
  border: 1px solid #fbe0e0;
}

.overdue-total {
  color: #b23a3a;
  font-weight: 700;
}

.overdue-empty {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 13px;
}

.overdue-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}

.overdue-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 0;
  background: #fef7f7;
  border: 1px solid #fbe0e0;
}

.overdue-view-btn {
  flex-shrink: 0;
}

.overdue-row-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.overdue-invoice-no {
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}

.overdue-customer {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.overdue-row-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}

.overdue-amount {
  font-size: 13px;
  font-weight: 700;
  color: #b23a3a;
}

.overdue-due-date {
  font-size: 11px;
  color: #94a3b8;
}
</style>
